/**
 * Kidify blog: product card on hover.
 *
 * On a desktop with a mouse, resting the pointer on (or tabbing to) a
 * product link inside a blog post shows that product's card next to the
 * link. The card is the product card template (CLAUDE.md): it comes from
 * /products/<handle>?view=kidify-card through fetchCard() and is drawn by
 * miniCard(), both from assets/kidify-shelf.js, so it shows the same
 * badges, price and stock line as every other card. Nothing here decides
 * a badge or price rule.
 *
 * The card is a picture only: it takes no pointer events, so it never
 * covers a neighbouring link, and a click goes to the text link itself.
 *
 * Phones and touch screens get nothing new: a tap still follows the link,
 * and the "Shop this guide" strip already shows the cards.
 */

const DESKTOP = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 990px)');
const SHOW_DELAY = 150;
const HIDE_DELAY = 150;
const GAP = 8;
const WIDTH = 220;

const handleOf = (link) => {
  if (link.origin !== window.location.origin) return null;
  const match = link.pathname.match(/\/products\/([^/]+)\/?$/);
  return match ? decodeURIComponent(match[1]) : null;
};

class LinkPreview {
  #pop = null;
  #link = null;
  #showTimer = 0;
  #hideTimer = 0;

  constructor(root) {
    this.root = root;
    root.addEventListener('pointerover', this.#onOver);
    root.addEventListener('pointerout', this.#onOut);
    root.addEventListener('focusin', this.#onOver);
    root.addEventListener('focusout', this.#onOut);
    document.addEventListener('keydown', (event) => event.key === 'Escape' && this.#hide());
    window.addEventListener('scroll', () => this.#hide(), { passive: true });
    window.addEventListener('resize', () => this.#hide());
  }

  #linkFrom(event) {
    const link = event.target.closest?.('a[href]');
    if (!link || !this.root.contains(link) || link.closest('.kd-shop')) return null;
    return handleOf(link) ? link : null;
  }

  #onOver = (event) => {
    if (!DESKTOP.matches) return;
    const link = this.#linkFrom(event);
    if (!link) return;
    clearTimeout(this.#hideTimer);
    if (link === this.#link) return;
    // A different link: drop the old card at once so it can't land late.
    this.#hide();
    // Start loading now; the short delay only stops cards flashing up as the pointer passes.
    window.Kidify?.fetchCard?.(handleOf(link));
    this.#showTimer = setTimeout(() => this.#show(link), SHOW_DELAY);
  };

  #onOut = (event) => {
    const link = this.#linkFrom(event);
    if (!link) return;
    if (event.relatedTarget && link.contains(event.relatedTarget)) return;
    clearTimeout(this.#showTimer);
    this.#scheduleHide();
  };

  #scheduleHide() {
    clearTimeout(this.#hideTimer);
    this.#hideTimer = setTimeout(() => this.#hide(), HIDE_DELAY);
  }

  async #show(link) {
    const kidify = window.Kidify;
    if (!kidify?.fetchCard || !kidify?.miniCard) return;
    this.#link = link;
    const handle = handleOf(link);
    const { card } = await kidify.fetchCard(handle);
    // The pointer may have moved on while the card loaded.
    if (this.#link !== link || !card) return;

    const pop = this.#popover();
    pop.innerHTML = kidify.miniCard({ card, entry: { handle } });
    // The text link already goes to the product; keep the card out of the tab order.
    pop.querySelectorAll('a').forEach((a) => a.setAttribute('tabindex', '-1'));
    pop.querySelectorAll('img').forEach((img) => (img.loading = 'eager'));
    pop.hidden = false;
    this.#place(link, pop);
  }

  #hide() {
    clearTimeout(this.#showTimer);
    this.#link = null;
    if (this.#pop) this.#pop.hidden = true;
  }

  #popover() {
    if (this.#pop) return this.#pop;
    const pop = document.createElement('div');
    pop.className = 'kidify-link-preview';
    pop.setAttribute('aria-hidden', 'true');
    pop.hidden = true;
    document.body.append(pop);
    this.#pop = pop;
    return pop;
  }

  /** Below the link if it fits, otherwise above; kept inside the window. */
  #place(link, pop) {
    // A link can wrap onto two lines; anchor to its first line.
    const rects = [...link.getClientRects()];
    const rect = rects[0] || link.getBoundingClientRect();
    const height = pop.offsetHeight;
    const below = rect.bottom + GAP;
    const top = below + height <= window.innerHeight - GAP ? below : Math.max(GAP, rect.top - GAP - height);
    const left = Math.min(Math.max(GAP, rect.left), window.innerWidth - WIDTH - GAP);
    pop.style.top = `${Math.round(top)}px`;
    pop.style.left = `${Math.round(left)}px`;
  }
}

document.querySelectorAll('.blog-post-content').forEach((root) => new LinkPreview(root));
